// Bot Error Logs Viewer Modal (WP-07).
// Lists recent delivery and automation errors with 30-day retention note.
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../lib/i18n';
import { botErrorList, type BotErrorDoc } from '../../lib/bots';
import { Button } from '../ui/Button';
import { CloseIcon, RefreshIcon, AlertTriangleIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface BotErrorLogsModalProps {
  open: boolean;
  integrationId: string;
  botLabel?: string;
  onClose: () => void;
}

export default function BotErrorLogsModal({
  open,
  integrationId,
  botLabel,
  onClose,
}: BotErrorLogsModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [errors, setErrors] = useState<BotErrorDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchErrors = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      const res = await botErrorList({ integrationId, limit: 50 });
      if (res.data?.ok && Array.isArray(res.data.errors)) {
        setErrors(res.data.errors);
      }
    } catch (err) {
      console.error('[BotErrorLogsModal] load error', err);
      toast.error((err as Error).message || 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (open) {
      fetchErrors();
    }
  }, [open, fetchErrors]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bot-error-logs-title"
        className="relative w-full max-w-3xl rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-2">
              <h3 id="bot-error-logs-title" className="text-base font-bold text-ink dark:text-white">
                {t('bots.errors.title') || 'Bot Error Logs'}
              </h3>
              {botLabel && (
                <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">
                  {botLabel}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
              {t('bots.errors.subtitle') ||
                'Meta Cloud API rejections, template delivery failures, and compliance blocks.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={fetchErrors}
              loading={loading}
              leadingIcon={<RefreshIcon className="h-3.5 w-3.5" />}
            >
              {t('common.refresh') || 'Refresh'}
            </Button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close') || 'Close'}
              className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 30-day retention notice banner */}
        <div className="border-b border-line bg-blue-50/70 px-5 py-2.5 dark:border-slate-800 dark:bg-blue-950/20 text-xs flex items-center gap-2 text-blue-800 dark:text-blue-300">
          <AlertTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            {t('bots.errors.retentionNotice') ||
              'Retention Notice: Error logs are automatically swept and purged after 30 days.'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 text-xs">
          {loading && errors.length === 0 ? (
            <div className="grid h-48 place-items-center">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            </div>
          ) : errors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line p-12 text-center text-ink-3 dark:border-slate-800 dark:text-slate-400">
              <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40">
                ✓
              </div>
              <p className="font-semibold text-ink dark:text-slate-200">
                {t('bots.errors.emptyTitle') || 'No Recent Errors'}
              </p>
              <p className="mt-1 text-[11px]">
                {t('bots.errors.emptyDesc') || 'All bot automations and sends are healthy for this number.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {errors.map((err) => {
                const isExpanded = expandedId === err.id;
                const formattedDate = new Date(err.at).toLocaleString();

                return (
                  <div
                    key={err.id}
                    className="rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/40 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-pink-50 px-2 py-0.5 font-mono text-[10px] font-bold text-danger dark:bg-pink-950/50 dark:text-pink-300">
                            {err.code}
                          </span>
                          <span className="text-[11px] text-ink-3 dark:text-slate-400">
                            {formattedDate}
                          </span>
                        </div>
                        <p className="font-medium text-ink dark:text-slate-200">{err.message}</p>
                      </div>

                      {err.context && (
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : err.id)}
                          className="rounded px-2 py-1 text-[11px] font-medium text-brand hover:bg-brand/10 transition-colors"
                        >
                          {isExpanded
                            ? t('common.hide') || 'Hide Details'
                            : t('common.details') || 'Details'}
                        </button>
                      )}
                    </div>

                    {isExpanded && err.context && (
                      <div className="mt-3 rounded-lg border border-line/60 bg-slate-900 p-3 font-mono text-[11px] text-slate-200 dark:border-slate-700">
                        <pre className="overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(err.context, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.close') || 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
